/**
 * HUD element outlines for UI movers (W2-02; DESIGN.md §4.8, rendering.md R§7.3): a line with `attachUI` moves a HUD
 * element (score, combo, pause, bar, name, level) instead of drawing itself. The outline shows the element it moves,
 * where it is now (the attach transform applied), with "Moves: score". Drawn for the active and selected UI movers,
 * and for every UI mover while "show invisible lines" is on.
 */

import type { AppServices } from '../../../app/registry/types';
import type { HudElementDraw, HudElementId } from '../../../core/engine/types';
import { Mat2 } from '../../../core/math/mat2d';
import type { UiElement } from '../../../core/model/types';
import type { OverlayDrawContext, PreviewOverlayProvider } from '../../../render/types';
import { m } from '../preview.messages';
import { dp, drawLabel, pathQuad } from './shared';
import { overlayTheme } from './theme';

/** UI units of one HUD text size unit (glyph PxScale 0.04·size·W px over W/2 px per unit). */
const HUD_EM = 0.08;
/** Ascent over PxScale of the HUD fonts (phigros.ttf 0.8; the UI font a little higher). */
const ASCENT = 0.8;

const ELEMENT_KEY: Readonly<Record<HudElementId, string>> = {
  pause: m.hudPause,
  combonumber: m.hudComboNumber,
  combo: m.hudCombo,
  score: m.hudScore,
  bar: m.hudBar,
  name: m.hudName,
  level: m.hudLevel,
  accuracy: m.hudScore,
};

export function hudElementKey(id: HudElementId): string {
  return ELEMENT_KEY[id];
}

/** Bounding box of an element in its own UI space (before the attach matrix): [x0, y0, x1, y1]. */
export function hudElementBox(
  c: OverlayDrawContext,
  e: HudElementDraw,
): readonly [number, number, number, number] | null {
  if (e.rects !== undefined && e.rects.length > 0) {
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    for (const [x, y, w, h] of e.rects) {
      x0 = Math.min(x0, x, x + w);
      y0 = Math.min(y0, y, y + h);
      x1 = Math.max(x1, x, x + w);
      y1 = Math.max(y1, y, y + h);
    }
    return [x0, y0, x1, y1];
  }
  if (e.text === undefined || e.text === '') return null;
  const size = e.size ?? 1;
  const unitPx = c.viewport.W / 2;
  const fontPx = Math.max(2, HUD_EM * size * unitPx);
  c.ctx.save();
  c.ctx.font = `${String(fontPx)}px ${overlayTheme().fontSans || 'sans-serif'}`;
  let w = c.ctx.measureText(e.text).width / unitPx;
  c.ctx.restore();
  if (e.maxWidth !== undefined) w = Math.min(w, e.maxWidth);
  const h = HUD_EM * size * ASCENT;
  const x0 = e.pos[0] - e.anchor[0] * w;
  const y0 = e.pos[1] - e.anchor[1] * h;
  return [x0, y0, x0 + w, y0 + h];
}

export function createHudOutlineOverlay(services: AppServices): PreviewOverlayProvider {
  return {
    id: 'preview.hudOutline',
    order: 45,
    draw(c) {
      const store = services.store;
      const hud = c.list.hud;
      if (store === null || hud === null) return;
      const st = services.editor.getState();
      const all = st.preview.showInvisibleLines;
      const wanted = new Map<UiElement, boolean>();
      for (const s of c.lines) {
        if (!s.uiMover) continue;
        const el = store.doc.lines.get(s.line)?.attachUI ?? null;
        if (el === null) continue;
        const strong = s.line === st.activeLine || st.selection.lines.has(s.line);
        if (!strong && !all) continue;
        wanted.set(el, (wanted.get(el) ?? false) || strong);
      }
      if (wanted.size === 0) return;
      const theme = overlayTheme();
      const ctx = c.ctx;
      for (const e of hud.elements) {
        if (e.id === 'accuracy') continue;
        const strong = wanted.get(e.id);
        if (strong === undefined) continue;
        const box = hudElementBox(c, e);
        if (box === null) continue;
        const pad = 0.006;
        const pts: number[] = [];
        for (const [x, y] of [
          [box[0] - pad, box[1] - pad],
          [box[2] + pad, box[1] - pad],
          [box[2] + pad, box[3] + pad],
          [box[0] - pad, box[3] + pad],
        ] as const) {
          const [ux, uy] = Mat2.apply(e.m, x, y);
          const [px, py] = c.uiToPx(ux, uy);
          pts.push(px, py);
        }
        ctx.save();
        ctx.strokeStyle = strong ? theme.accent : theme.invisible;
        ctx.lineWidth = dp(c, strong ? 1.75 : 1.25);
        ctx.setLineDash([dp(c, 5), dp(c, 3)]);
        pathQuad(ctx, pts);
        ctx.stroke();
        ctx.restore();
        const lx = Math.min(pts[0] ?? 0, pts[6] ?? 0);
        const ly = Math.max(pts[5] ?? 0, pts[7] ?? 0) + dp(c, 4);
        drawLabel(c, services.i18n.t(m.hudMoves, { element: services.i18n.t(hudElementKey(e.id)) }), lx, ly, {
          fg: theme.text,
          bg: theme.surface,
          border: strong ? theme.accent : theme.invisible,
          size: 10,
        });
      }
    },
  };
}
